// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { Suspense, useEffect } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { PageLoader } from "../components/layout/PageLoader";
import { LoginPage } from "../features/auth/pages/LoginPage";
import { navigateTo } from "../router/navigation";
import { FullPageFrame, FullPageLoader } from "./FullPageLoader";
import { consumeReturnTo } from "./returnTo";
import { useAuthGate } from "./useAuthGate";

const useStyles = makeStyles({
  expiryNotice: {
    color: tokens.colorPaletteRedForeground1,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    textAlign: "center",
  },
});

export function PublicLoginRoute() {
  const styles = useStyles();
  const { devPreview, isAuthorized, isProcessing, isCheckingSession, isSessionExpired } =
    useAuthGate();
  const shouldEnterProduct = !devPreview && !isProcessing && isAuthorized;

  useEffect(() => {
    if (shouldEnterProduct) {
      navigateTo(consumeReturnTo() ?? "/insights", { replace: true });
    }
  }, [shouldEnterProduct]);

  if (isCheckingSession) {
    return <FullPageLoader />;
  }

  return (
    <FullPageFrame>
      {isSessionExpired && (
        <div className={styles.expiryNotice} role="alert">
          Your session has expired. Please sign in again to continue.
        </div>
      )}
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    </FullPageFrame>
  );
}
